import { io } from 'socket.io-client';
import 'react-toastify/dist/ReactToastify.css';
import { showGlobalNotification } from '../context/NotificationContext';
import { getWebAlertSoundUrl, getWebAlertVolume } from './webAlertSound';

let socket;

export const initSocket = (token) => {
  // Close any existing connections
  if (socket) {
    // Detach the old socket first: closing it must not fire "socket-disconnected", which
    // schedules another initSocket and so another close, a reconnect loop every few seconds
    socket.removeAllListeners();
    socket.disconnect();
  }

  // console.log('Initializing new socket connection with token:', token ? 'Token provided' : 'No token'); // Removed log
  
  // Create new connection with authentication
  socket = io(process.env.NODE_ENV === 'production' 
    ? window.location.origin 
    : 'http://localhost:5000', {
    auth: {
      token
    },
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000
  });

  // Set up event listeners
  socket.on('connect', () => {
    // console.log('Socket connected successfully with ID:', socket.id); // Removed log
    
    // Store socket ID in window for debugging
    window.socketId = socket.id;
    
    // Dispatch a custom event that other components can listen for
    window.dispatchEvent(new CustomEvent('socket-connected', { 
      detail: { socketId: socket.id } 
    }));
  });

  socket.on('disconnect', (reason) => {
    // console.log('Socket disconnected. Reason:', reason); // Removed log
    
    // Dispatch a custom event that other components can listen for
    window.dispatchEvent(new CustomEvent('socket-disconnected', { 
      detail: { reason } 
    }));
  });

  socket.on('connect_error', (error) => {
    // console.error('Socket connection error:', error.message); // Removed log
  });

  socket.on('error', (error) => {
    // console.error('Socket error:', error); // Removed log
  });
  
  // The server sends "connectionTest" every time a socket connects. It is only a handshake
  // confirmation and must stay silent: it used to play the notification sound, so any
  // reconnect (a network blip, a sleeping laptop) made the bell ring.
  socket.on('connectionTest', () => {});

  // Job assignment notification
  socket.on('jobAssigned', (data) => {
    // console.log('%c Socket: jobAssigned event received', 'background: #ff9800; color: white; padding: 2px 5px; border-radius: 2px;', data); // Removed log
    
    // Validate the data structure
    if (!data || !data.jobDetails) {
      // console.error('Invalid jobAssigned data received:', data); // Removed log
      return;
    }
    
    // Create a timestamp for this event
    const timestamp = new Date().toISOString();
    // console.log(`[${timestamp}] Processing jobAssigned event`); // Removed log
    
    // Store the event in sessionStorage for debugging and for later playback if audio isn't unlocked
    try {
      const storedEvents = JSON.parse(sessionStorage.getItem('socketEvents') || '[]');
      storedEvents.push({
        type: 'jobAssigned',
        timestamp,
        data,
        soundPlayed: false // Track if sound has been played for this event
      });
      sessionStorage.setItem('socketEvents', JSON.stringify(storedEvents.slice(-10))); // Keep last 10 events
      
      // Set a flag to indicate there's a pending notification sound
      sessionStorage.setItem('pendingNotificationSound', 'true');
    } catch (storageError) {
      // console.error('Error storing event in sessionStorage:', storageError); // Removed log
    }
    
    // Try to use the window.socketEventHandlers first (set in App.js)
    if (window.socketEventHandlers && typeof window.socketEventHandlers.jobAssigned === 'function') {
      // console.log('Socket: Using window.socketEventHandlers.jobAssigned'); // Removed log
      try {
        window.socketEventHandlers.jobAssigned(data);
      } catch (handlerError) {
        // console.error('Error using window.socketEventHandlers.jobAssigned:', handlerError); // Removed log
        // Fall back to showGlobalNotification
        fallbackToGlobalNotification(data);
      }
    } else {
      // Fall back to showGlobalNotification
      fallbackToGlobalNotification(data);
    }
    
    try {
      // Dispatch custom event for backward compatibility
      // console.log('Socket: Dispatching job-assigned custom event'); // Removed log
      window.dispatchEvent(new CustomEvent('job-assigned', { detail: data }));
    } catch (eventError) {
      // console.error('Error dispatching custom event:', eventError); // Removed log
    }
    
    // Check if audio is unlocked before trying to play sound
    const isAudioUnlocked = localStorage.getItem('audioContextUnlocked') === 'true';
    
    if (isAudioUnlocked) {
      // console.log('Socket: Audio is unlocked, playing notification sound immediately'); // Removed log
      // Play notification sound directly to ensure it plays
      playNotificationSound();
      
      // Mark this event as having played sound
      try {
        const storedEvents = JSON.parse(sessionStorage.getItem('socketEvents') || '[]');
        const updatedEvents = storedEvents.map(event => {
          if (event.type === 'jobAssigned' && event.timestamp === timestamp) {
            return { ...event, soundPlayed: true };
          }
          return event;
        });
        sessionStorage.setItem('socketEvents', JSON.stringify(updatedEvents));
      } catch (updateError) {
        // console.error('Error updating stored event sound status:', updateError); // Removed log
      }
    } else {
      // console.log('Socket: Audio is not unlocked, sound will play on next user interaction'); // Removed log
      
      // Set up a one-time event listener to play sound on next user interaction
      const playPendingSound = () => {
        // console.log('Socket: User interaction detected, playing pending notification sound'); // Removed log
        playNotificationSound();
        
        // Mark this event as having played sound
        try {
          const storedEvents = JSON.parse(sessionStorage.getItem('socketEvents') || '[]');
          const updatedEvents = storedEvents.map(event => {
            if (event.type === 'jobAssigned' && !event.soundPlayed) {
              return { ...event, soundPlayed: true };
            }
            return event;
          });
          sessionStorage.setItem('socketEvents', JSON.stringify(updatedEvents));
          
          // Clear the pending notification sound flag
          sessionStorage.removeItem('pendingNotificationSound');
        } catch (updateError) {
          // console.error('Error updating stored event sound status:', updateError); // Removed log
        }
        
        // Remove the event listeners after playing the sound
        document.removeEventListener('click', playPendingSound);
        document.removeEventListener('touchstart', playPendingSound);
        document.removeEventListener('keydown', playPendingSound);
      };
      
      // Add event listeners for user interaction
      document.addEventListener('click', playPendingSound, { once: true });
      document.addEventListener('touchstart', playPendingSound, { once: true });
      document.addEventListener('keydown', playPendingSound, { once: true });
    }
  });
  
  // Helper function to fall back to showGlobalNotification
  const fallbackToGlobalNotification = (data) => {
    // console.log('Socket: Falling back to showGlobalNotification'); // Removed log
    try {
      // Show global notification (new)
      // console.log('Socket: Calling showGlobalNotification with:', { // Removed log
      //   message: `New job assigned: ${data.jobDetails.service} at ${data.jobDetails.location}`, // Removed log
      //   severity: 'info', // Removed log
      //   jobId: data.jobId // Removed log
      // }); // Removed log
      
      // Call the global notification function
      showGlobalNotification(
        `New job assigned: ${data.jobDetails.service} at ${data.jobDetails.location}`,
        'info',
        data.jobId
      );
    } catch (notificationError) {
      // console.error('Error showing global notification:', notificationError); // Removed log
    }
  };
  
  // Enhanced function to play notification sound - with multiple methods and fallbacks
  const playNotificationSound = () => {
    const soundUrl = getWebAlertSoundUrl();
    if (!soundUrl) return; // user chose "None"
    const volume = getWebAlertVolume();
    if (volume <= 0) return;
    // console.log('Socket: Attempting to play notification sound with enhanced reliability'); // Removed log
    
    // Track if sound played successfully with any method
    let soundPlayed = false;
    
    // Method 1: Use notification context if available (preferred)
    if (window.notificationContext && typeof window.notificationContext.showNotification === 'function') {
      // console.log('Socket: Method 1 - Using notification context for sound playback'); // Removed log
      try {
        // This typically handles audio in the context
        window.notificationContext.playSound && window.notificationContext.playSound();
        soundPlayed = true;
        // console.log('Socket: Method 1 succeeded'); // Removed log
      } catch (error) {
        // console.error('Socket: Method 1 failed:', error); // Removed log
      }
    }
    
    // Method 2: Standard Audio API (if method 1 didn't work or is unavailable)
    if (!soundPlayed) {
      // console.log('Socket: Method 2 - Using standard Audio API'); // Removed log
      try {
        const audio = new Audio(soundUrl);
        // Preload the audio
        audio.preload = 'auto';
        
        // Add event listeners for debugging
        // audio.addEventListener('play', () => console.log('Socket: Audio play event fired')); // Removed log
        // audio.addEventListener('playing', () => console.log('Socket: Audio playing event fired')); // Removed log
        // audio.addEventListener('ended', () => console.log('Socket: Audio ended event fired')); // Removed log
        // audio.addEventListener('error', (e) => console.error('Socket: Audio error event:', e)); // Removed log
        
        audio.volume = volume;
        
        // Try to play the sound
        const playPromise = audio.play();
        
        // Modern browsers return a promise from play()
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              // console.log('Socket: Method 2 succeeded'); // Removed log
              soundPlayed = true;
              // Mark audio as unlocked for future reference
              localStorage.setItem('audioContextUnlocked', 'true');
            })
            .catch(err => {
              // console.error('Socket: Method 2 failed:', err); // Removed log
              // console.log('Socket: Trying fallback methods'); // Removed log
            });
        } else {
          // Older browsers don't return a promise
          // console.log('Socket: Browser didn\'t return a play promise, assuming successful playback'); // Removed log
          soundPlayed = true;
        }
      } catch (error) {
        // console.error('Socket: Method 2 complete failure:', error); // Removed log
      }
    }
    
    // Method 3: Create and trigger an audio element in the DOM (last resort)
    if (!soundPlayed) {
      // console.log('Socket: Method 3 - Creating and triggering DOM audio element'); // Removed log
      try {
        // Create audio element and append to DOM
        const audioElement = document.createElement('audio');
        audioElement.src = soundUrl;
        audioElement.volume = volume;
        audioElement.id = 'notification-sound-' + Date.now();
        audioElement.style.display = 'none';
        document.body.appendChild(audioElement);
        
        // Add event listeners
        // audioElement.addEventListener('play', () => console.log('Socket: DOM Audio play event fired')); // Removed log
        // audioElement.addEventListener('playing', () => console.log('Socket: DOM Audio playing event fired')); // Removed log
        audioElement.addEventListener('ended', () => {
          // console.log('Socket: DOM Audio ended event fired'); // Removed log
          // Clean up - remove element after playback
          document.body.removeChild(audioElement);
        });
        audioElement.addEventListener('error', (e) => {
          // console.error('Socket: DOM Audio error event:', e); // Removed log
          // Clean up on error too
          document.body.removeChild(audioElement);
        });
        
        // Try to play
        const domPlayPromise = audioElement.play();
        if (domPlayPromise !== undefined) {
          domPlayPromise
            .then(() => {
              // console.log('Socket: Method 3 succeeded'); // Removed log
              soundPlayed = true;
            })
            .catch(err => {
              // console.error('Socket: Method 3 failed:', err); // Removed log
              // Clean up if failed
              if (document.body.contains(audioElement)) {
                document.body.removeChild(audioElement);
              }
            });
        }
      } catch (error) {
        // console.error('Socket: Method 3 complete failure:', error); // Removed log
      }
    }
    
    // Log final result
    setTimeout(() => {
      // console.log(`Socket: Notification sound playback ${soundPlayed ? 'succeeded' : 'failed'} with available methods`); // Removed log
      
      if (!soundPlayed) {
        // console.log('Socket: All sound playback methods failed. This might be due to browser autoplay restrictions.'); // Removed log
        
        // Store the failure for debugging
        try {
          sessionStorage.setItem('lastSoundPlaybackFailed', 'true');
          sessionStorage.setItem('lastSoundPlaybackAttempt', new Date().toISOString());
        } catch (e) {
          // console.error('Socket: Failed to store sound playback status:', e); // Removed log
        }
      }
    }, 500); // Short delay to allow promises to resolve
    
    return soundPlayed;
  };
  
  // Job auto-rejection notification
  // Whether the logged-in user has the given simplified notification event
  // enabled. Socket toasts previously ignored preferences entirely, so
  // unchecking e.g. Job Rejected still toasted on every auto-rejection.
  const eventToastEnabled = (eventKey) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      const prefs = user?.notificationPreferences;
      if (!prefs) return true; // no saved prefs → default on
      const adminBuckets = prefs.admin?.events?.[eventKey];
      const adminEnabled = adminBuckets
        ? (adminBuckets.all?.enabled ?? adminBuckets.forVendor?.enabled ?? adminBuckets.unassigned?.enabled)
        : undefined;
      const selfEnabled = prefs.self?.events?.[eventKey]?.enabled;
      // Explicitly disabled in whichever bucket applies → silence the toast
      const effective = adminEnabled !== undefined ? adminEnabled : selfEnabled;
      return effective !== false;
    } catch {
      return true;
    }
  };

  socket.on('jobAutoRejected', (data) => {
    if (eventToastEnabled('jobRejected')) {
      showGlobalNotification(
        `Job auto-rejected: ${data.message}`,
        'error',
        data.jobId
      );
    }

    // Dispatch custom event for backward compatibility
    window.dispatchEvent(new CustomEvent('job-auto-rejected', { detail: data }));
  });

  // Job acceptance notification
  socket.on('jobAccepted', (data) => {
    if (eventToastEnabled('jobAccepted')) {
      showGlobalNotification(
        `Job accepted: ${data.message}`,
        'success',
        data.jobId
      );
    }

    // Dispatch custom event for backward compatibility
    window.dispatchEvent(new CustomEvent('job-accepted', { detail: data }));
  });

  // Job rejection notification
  socket.on('jobRejected', (data) => {
    if (eventToastEnabled('jobRejected')) {
      showGlobalNotification(
        `Job rejected: ${data.message}`,
        'warning',
        data.jobId
      );
    }

    // Dispatch custom event for backward compatibility
    window.dispatchEvent(new CustomEvent('job-rejected', { detail: data }));
  });

  // General job update notification
  // console.log('SOCKET DEBUG: Attaching listener for jobUpdated event'); // Removed log
  socket.on('jobUpdated', (data) => {
    // console.log('SOCKET DEBUG: Raw jobUpdated event received from server:', data); // Removed log
    // No toast notification for general updates to avoid too many notifications
    // Just dispatch the event to update the UI
    window.dispatchEvent(new CustomEvent('jobUpdated', { detail: data }));
  });

  // Wildcard listener for debugging ALL incoming events
  // socket.onAny((eventName, ...args) => { // Removed wildcard listener
  //   console.log(`SOCKET DEBUG (ANY): Received event: ${eventName}`, args);
  // });

  return socket;
};

export const getSocket = () => socket;

export const disconnectSocket = () => {
  if (socket) {
    // console.log('Explicitly disconnecting socket:', socket.id); // Removed log
    socket.disconnect();
    socket = null; // Clear the socket reference
    
    // Clear any socket-related data from storage
    sessionStorage.removeItem('socketEvents');
    sessionStorage.removeItem('showNotification');
    sessionStorage.removeItem('pendingNotificationSound');
    
    // Remove socket ID from window
    if (window.socketId) {
      delete window.socketId;
    }
    
    // console.log('Socket disconnected and cleaned up'); // Removed log
  }
};
